import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const money  = (n) => `${Number(n||0).toLocaleString('en-US', { minimumFractionDigits:0 })} RWF`
const num    = (n) => Number(n||0).toLocaleString()
const pct    = (a, b) => b ? `${((a/b)*100).toFixed(1)}%` : '0%'
const trend  = (cur, prev) => prev ? (((cur-prev)/prev)*100).toFixed(1) : null

const PERIODS = [
  { id:'7d',   label:'7 days'   },
  { id:'30d',  label:'30 days'  },
  { id:'90d',  label:'90 days'  },
  { id:'1y',   label:'1 year'   },
]

// ── Sparkline (pure SVG) ──────────────────────────────────────────────────────
function Sparkline({ data = [], color = '#e8440a', height = 40, fill = false }) {
  if (!data.length) return null
  const max = Math.max(...data, 1)
  const min = Math.min(...data)
  const range = max - min || 1
  const W = 120, H = height
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W
    const y = H - ((v - min) / range) * (H - 4) - 2
    return `${x},${y}`
  }).join(' ')

  const area = `0,${H} ${pts} ${W},${H}`

  return (
    <svg width={W} height={H} style={{ overflow:'visible' }}>
      {fill && <polygon points={area} fill={`${color}18`} />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ── Bar chart (pure SVG) ──────────────────────────────────────────────────────
function BarChart({ data = [], color = '#e8440a', height = 160, showLabels = true, labelKey = 'label', valueKey = 'value', formatValue = num }) {
  if (!data.length) return <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'#9a9a94', fontSize:12 }}>No data</div>
  const max = Math.max(...data.map(d => d[valueKey]), 1)
  const barW = Math.max(8, Math.min(40, (500 / data.length) - 6))

  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:4, height, width:'100%' }}>
      {data.map((d, i) => {
        const h = Math.max(4, (d[valueKey] / max) * (height - 24))
        return (
          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor:'default', position:'relative' }}
            title={`${d[labelKey]}: ${formatValue(d[valueKey])}`}>
            <div style={{ width:'100%', height:h, background:color, borderRadius:'3px 3px 0 0', opacity:0.9, transition:'opacity 0.15s', minWidth:4 }}
              onMouseEnter={e => e.currentTarget.style.opacity='1'}
              onMouseLeave={e => e.currentTarget.style.opacity='0.9'} />
            {showLabels && data.length <= 14 && (
              <div style={{ fontSize:9, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", transform:'rotate(-30deg)', transformOrigin:'top center', whiteSpace:'nowrap', marginTop:2 }}>
                {d[labelKey]}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Donut chart (SVG) ─────────────────────────────────────────────────────────
function DonutChart({ segments = [], size = 120, thickness = 22 }) {
  if (!segments.length) return null
  const total   = segments.reduce((s, d) => s + d.value, 0) || 1
  const R  = (size - thickness) / 2
  const cx = size / 2
  const cy = size / 2
  let   cumAngle = -Math.PI / 2

  const arcs = segments.map(seg => {
    const angle = (seg.value / total) * 2 * Math.PI
    const x1    = cx + R * Math.cos(cumAngle)
    const y1    = cy + R * Math.sin(cumAngle)
    cumAngle   += angle
    const x2    = cx + R * Math.cos(cumAngle)
    const y2    = cy + R * Math.sin(cumAngle)
    const large = angle > Math.PI ? 1 : 0
    return { ...seg, d: `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`, angle }
  })

  return (
    <svg width={size} height={size}>
      {arcs.map((arc, i) => (
        <path key={i} d={arc.d} fill="none" stroke={arc.color} strokeWidth={thickness} strokeLinecap="round" style={{ transition:'stroke-dashoffset 0.6s' }}>
          <title>{arc.label}: {arc.value.toLocaleString()} ({pct(arc.value, total)})</title>
        </path>
      ))}
    </svg>
  )
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, trend: trendVal, sparkData, color = '#e8440a', icon, wide }) {
  const up = trendVal > 0
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, padding:'18px 20px', flex: wide ? 2 : 1, minWidth:160, position:'relative', overflow:'hidden' }}>
      {/* Subtle color accent */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:color, borderRadius:'12px 12px 0 0', opacity:0.7 }} />

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>{label}</div>
        {icon && <span style={{ fontSize:16, opacity:0.6 }}>{icon}</span>}
      </div>

      <div style={{ fontSize:28, fontWeight:800, letterSpacing:'-0.8px', color:'#111317', marginBottom:4, lineHeight:1 }}>{value}</div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
          {sub}
          {trendVal != null && (
            <span style={{ marginLeft:6, color: up ? '#15803d' : '#b91c1c', fontWeight:700 }}>
              {up ? '↑' : '↓'} {Math.abs(trendVal)}%
            </span>
          )}
        </div>
        {sparkData && <Sparkline data={sparkData} color={color} />}
      </div>
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children, action }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:12, overflow:'hidden', marginBottom:20 }}>
      <div style={{ padding:'14px 20px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fafaf8' }}>
        <div style={{ fontSize:13, fontWeight:800, color:'#111317' }}>{title}</div>
        {action}
      </div>
      <div style={{ padding:20 }}>{children}</div>
    </div>
  )
}

// ── Top item row ──────────────────────────────────────────────────────────────
function TopRow({ rank, name, sub, value, pctVal, color = '#e8440a' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid #f3f3f0' }}>
      <div style={{ width:22, height:22, borderRadius:5, background: rank === 1 ? '#111317' : '#f3f3f0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color: rank===1?'#fff':'#9a9a94', flexShrink:0 }}>{rank}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
        {sub && <div style={{ fontSize:10, color:'#9a9a94' }}>{sub}</div>}
      </div>
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:12, fontWeight:800, fontFamily:"'JetBrains Mono',monospace", color:'#111317' }}>{value}</div>
        {pctVal && <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{pctVal}</div>}
      </div>
      <div style={{ width:60, height:5, background:'#f3f3f0', borderRadius:3, flexShrink:0 }}>
        <div style={{ height:'100%', background:color, borderRadius:3, width:pctVal }} />
      </div>
    </div>
  )
}

// ── Main Analytics Page ───────────────────────────────────────────────────────
export default function Analytics() {
  const [period,   setPeriod]   = useState('30d')
  const [data,     setData]     = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState('')

  const fetchAnalytics = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const res = await api.get(`/analytics/admin/overview?period=${period}`)
      setData(res.data)
    } catch {
      setError('Using mock data')
      // ── Realistic mock data ──
      const days  = { '7d':7, '30d':30, '90d':90, '1y':365 }[period]
      const genSeries = (base, noise) => Array.from({ length: Math.min(days, 30) }, (_, i) => Math.max(0, base + Math.sin(i/3)*base*0.3 + (Math.random()-0.5)*noise))

      setData({
        overview: {
          revenue:       { value: 4820000, prev: 3900000, series: genSeries(160000, 40000) },
          orders:        { value: 1240,    prev: 1050,     series: genSeries(41, 10) },
          users:         { value: 8420,    prev: 7100,     series: genSeries(280, 60) },
          active_shops:  { value: 312,     prev: 280,      series: genSeries(10, 4) },
          reels:         { value: 2840,    prev: 2100,     series: genSeries(95, 25) },
          videos:        { value: 64,      prev: 48,       series: genSeries(2, 1) },
          live_streams:  { value: 186,     prev: 140,      series: genSeries(6, 3) },
          avg_order:     { value: 18500,   prev: 17200,    series: genSeries(615, 150) },
          success_rate:  { value: 78.4,    prev: 74.1,     series: genSeries(78, 5) },
        },
        revenue_chart: Array.from({ length: Math.min(days, 30) }, (_, i) => ({
          label: `D-${Math.min(days,30)-1-i}`,
          value: Math.floor(Math.random()*200000 + 80000),
          orders: Math.floor(Math.random()*50 + 20),
        })).reverse(),
        user_growth: Array.from({ length: Math.min(days, 30) }, (_, i) => ({
          label: `D-${Math.min(days,30)-1-i}`,
          buyers:  Math.floor(Math.random()*40 + 10),
          sellers: Math.floor(Math.random()*8 + 1),
          drivers: Math.floor(Math.random()*5 + 1),
        })).reverse(),
        top_shops: [
          { name:'Fashion House',   sub:'42 products',   revenue: 680000, orders: 94 },
          { name:'Tech Corner',     sub:'28 products',   revenue: 540000, orders: 72 },
          { name:'Beauty World',    sub:'61 products',   revenue: 420000, orders: 108 },
          { name:'Sports Arena',    sub:'35 products',   revenue: 310000, orders: 58 },
          { name:'Food & Drinks',   sub:'19 products',   revenue: 280000, orders: 143 },
        ],
        top_products: [
          { name:'iPhone 15 Case',   sub:'Tech Corner',    value: 48, revenue: 240000 },
          { name:'Kigali Dress',     sub:'Fashion House',  value: 42, revenue: 336000 },
          { name:'Face Serum',       sub:'Beauty World',   value: 38, revenue: 190000 },
          { name:'Sneakers XL',      sub:'Sports Arena',   value: 31, revenue: 248000 },
          { name:'Chapati Mix',      sub:'Food & Drinks',  value: 87, revenue: 87000  },
        ],
        payment_methods: [
          { label:'MTN MoMo', value:620, color:'#f59e0b' },
          { label:'Airtel',   value:380, color:'#ef4444' },
          { label:'iPay',     value:240, color:'#3b82f6' },
        ],
        order_status_dist: [
          { label:'Delivered',  value:820, color:'#16a34a' },
          { label:'Processing', value:185, color:'#7c3aed' },
          { label:'Pending',    value:142, color:'#f59e0b' },
          { label:'Cancelled',  value:93,  color:'#ef4444' },
        ],
        user_roles: [
          { label:'Buyers',  value:7200, color:'#1d4ed8' },
          { label:'Sellers', value:890,  color:'#e8440a' },
          { label:'Drivers', value:330,  color:'#15803d' },
        ],
        top_reels: [
          { name:'Summer Collection',  sub:'Fashion House', views:48200, likes:3200 },
          { name:'New Arrivals',       sub:'Tech Corner',   views:32800, likes:2100 },
          { name:'Skincare Routine',   sub:'Beauty World',  views:28400, likes:4200 },
          { name:'Goal of the Week',   sub:'Sports Arena',  views:24100, likes:1800 },
          { name:'Daily Specials',     sub:'Food & Drinks', views:19600, likes:890  },
        ],
        top_videos: [
          { name:'The Last Harvest',   sub:'Jean Creator',  views:15200, likes:980 },
          { name:'City Lights Ep. 3',  sub:'Ama Studios',   views:11800, likes:740 },
          { name:'Behind the Beat',    sub:'DJ Kivu',       views:9400,  likes:610 },
          { name:'Market Diaries',     sub:'Uwase Films',   views:7200,  likes:420 },
          { name:'Rwanda Untold',      sub:'Kigali Docs',   views:5100,  likes:310 },
        ],
      })
    } finally { setLoading(false) }
  }, [period])

  useEffect(() => { fetchAnalytics() }, [fetchAnalytics])

  if (loading) {
    return (
      <div style={{ padding:'28px 32px', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        <div style={{ display:'flex', gap:14, marginBottom:20 }}>
          {[1,2,3,4].map(i => <div key={i} style={{ flex:1, height:100, borderRadius:12, background:'#f3f3f0', animation:'pulse 1.5s infinite' }} />)}
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
      </div>
    )
  }

  const o = data?.overview || {}
  const topShopMax = Math.max(...(data?.top_shops?.map(s=>s.revenue)||[1]))

  return (
    <div style={{ padding:'28px 32px 80px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>Analytics</div>
          <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>Platform performance overview.</div>
        </div>
        <div style={{ display:'flex', gap:2, background:'#fff', border:'1px solid #e9e9e4', borderRadius:9, padding:4 }}>
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              style={{ padding:'7px 14px', borderRadius:6, border:'none', background:period===p.id?'#111317':'transparent', color:period===p.id?'#fff':'#9a9a94', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", transition:'all 0.15s' }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'8px 14px', marginBottom:16, fontSize:12, color:'#b45309' }}>⚠ {error}</div>}

      {/* ── ROW 1 — Primary KPIs ── */}
      <div style={{ display:'flex', gap:14, marginBottom:14, flexWrap:'wrap' }}>
        <KpiCard label="Total Revenue"   value={money(o.revenue?.value)}      sub={`prev: ${money(o.revenue?.prev)}`}      trend={trend(o.revenue?.value, o.revenue?.prev)}      sparkData={o.revenue?.series}      color="#e8440a" icon="💰" wide />
        <KpiCard label="Total Orders"    value={num(o.orders?.value)}          sub={`prev: ${num(o.orders?.prev)}`}          trend={trend(o.orders?.value, o.orders?.prev)}          sparkData={o.orders?.series}        color="#1d4ed8" icon="🛍" />
        <KpiCard label="Avg Order Value" value={money(o.avg_order?.value)}     sub={`prev: ${money(o.avg_order?.prev)}`}     trend={trend(o.avg_order?.value, o.avg_order?.prev)}     sparkData={o.avg_order?.series}     color="#15803d" icon="📊" />
        <KpiCard label="Payment Success" value={`${o.success_rate?.value||0}%`} sub={`prev: ${o.success_rate?.prev||0}%`}   trend={trend(o.success_rate?.value, o.success_rate?.prev)} sparkData={o.success_rate?.series} color="#7c3aed" icon="✓"  />
      </div>

      {/* ── ROW 2 — Secondary KPIs ── */}
      <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        <KpiCard label="Total Users"    value={num(o.users?.value)}          sub={`+${num((o.users?.value||0)-(o.users?.prev||0))} new`}      trend={trend(o.users?.value, o.users?.prev)}         sparkData={o.users?.series}        color="#0369a1" icon="👥" />
        <KpiCard label="Active Shops"   value={num(o.active_shops?.value)}   sub={`prev: ${num(o.active_shops?.prev)}`}                        trend={trend(o.active_shops?.value, o.active_shops?.prev)} sparkData={o.active_shops?.series} color="#b45309" icon="🏪" />
        <KpiCard label="Clips Posted"   value={num(o.reels?.value)}          sub={`prev: ${num(o.reels?.prev)}`}                               trend={trend(o.reels?.value, o.reels?.prev)}             sparkData={o.reels?.series}        color="#7c3aed" icon="▶" />
        <KpiCard label="Videos Posted"  value={num(o.videos?.value)}         sub={`prev: ${num(o.videos?.prev)}`}                              trend={trend(o.videos?.value, o.videos?.prev)}           sparkData={o.videos?.series}       color="#0d9488" icon="🎬" />
        <KpiCard label="Live Streams"   value={num(o.live_streams?.value)}   sub={`prev: ${num(o.live_streams?.prev)}`}                        trend={trend(o.live_streams?.value, o.live_streams?.prev)} sparkData={o.live_streams?.series} color="#ef4444" icon="🔴" />
      </div>

      {/* ── ROW 3 — Revenue chart + distributions ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:20 }}>

        {/* Revenue bar chart */}
        <Section title="Revenue Over Time">
          <BarChart data={data?.revenue_chart || []} color="#e8440a" height={140} formatValue={money} />
        </Section>

        {/* Payment methods donut */}
        <Section title="Payment Methods">
          <div style={{ display:'flex', alignItems:'center', gap:20 }}>
            <DonutChart segments={data?.payment_methods || []} size={110} thickness={20} />
            <div style={{ flex:1 }}>
              {(data?.payment_methods || []).map((m, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #f3f3f0' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:m.color, display:'inline-block' }} />
                    <span style={{ fontSize:12, fontWeight:600 }}>{m.label}</span>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{num(m.value)}</div>
                    <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>
                      {pct(m.value, (data?.payment_methods||[]).reduce((s,x)=>s+x.value,0))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Order status distribution */}
        <Section title="Order Status">
          <div style={{ display:'flex', alignItems:'center', gap:20 }}>
            <DonutChart segments={data?.order_status_dist || []} size={110} thickness={20} />
            <div style={{ flex:1 }}>
              {(data?.order_status_dist || []).map((s, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #f3f3f0' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:s.color, display:'inline-block' }} />
                    <span style={{ fontSize:12, fontWeight:600 }}>{s.label}</span>
                  </div>
                  <div style={{ fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace" }}>{num(s.value)}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* ── ROW 4 — User growth + user roles ── */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:20 }}>

        {/* User growth chart */}
        <Section title="New User Registrations"
          action={
            <div style={{ display:'flex', gap:12 }}>
              {[['Buyers','#1d4ed8'],['Sellers','#e8440a'],['Drivers','#15803d']].map(([l,c]) => (
                <div key={l} style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ width:8, height:8, borderRadius:2, background:c, display:'inline-block' }} />
                  <span style={{ fontSize:11, color:'#9a9a94' }}>{l}</span>
                </div>
              ))}
            </div>
          }>
          <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:120 }}>
            {(data?.user_growth || []).map((d, i) => {
              const maxVal = Math.max(...(data?.user_growth||[]).map(x => x.buyers+x.sellers+x.drivers), 1)
              const total  = d.buyers + d.sellers + d.drivers
              const scale  = (total / maxVal) * 114
              return (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:0 }} title={`${d.label}: ${total} new users`}>
                  <div style={{ width:'100%', display:'flex', flexDirection:'column', borderRadius:'3px 3px 0 0', overflow:'hidden', minWidth:3 }}>
                    <div style={{ height: (d.buyers/total)*scale,  background:'#1d4ed8', minHeight: d.buyers>0?2:0 }} />
                    <div style={{ height: (d.sellers/total)*scale, background:'#e8440a', minHeight: d.sellers>0?2:0 }} />
                    <div style={{ height: (d.drivers/total)*scale, background:'#15803d', minHeight: d.drivers>0?2:0 }} />
                  </div>
                </div>
              )
            })}
          </div>
        </Section>

        {/* User role distribution */}
        <Section title="User Breakdown">
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
            <DonutChart segments={data?.user_roles || []} size={120} thickness={24} />
            <div style={{ width:'100%' }}>
              {(data?.user_roles || []).map((r, i) => {
                const total = (data?.user_roles||[]).reduce((s,x)=>s+x.value,0) || 1
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f3f3f0' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ width:9, height:9, borderRadius:'50%', background:r.color, display:'inline-block' }} />
                      <span style={{ fontSize:12, fontWeight:600 }}>{r.label}</span>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <div style={{ width:60, height:4, background:'#f3f3f0', borderRadius:2 }}>
                        <div style={{ width:`${(r.value/total)*100}%`, height:'100%', background:r.color, borderRadius:2 }} />
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:'#111317', minWidth:40, textAlign:'right' }}>{num(r.value)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Section>
      </div>

      {/* ── ROW 5 — Top lists ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>

        {/* Top shops */}
        <Section title="Top Shops by Revenue">
          {(data?.top_shops || []).map((s, i) => (
            <TopRow key={i} rank={i+1} name={s.name} sub={s.sub}
              value={money(s.revenue)}
              pctVal={`${((s.revenue/topShopMax)*100).toFixed(0)}%`}
              color="#e8440a" />
          ))}
        </Section>

        {/* Top products */}
        <Section title="Top Products by Orders">
          {(data?.top_products || []).map((p, i) => (
            <TopRow key={i} rank={i+1} name={p.name} sub={p.sub}
              value={`${num(p.value)} orders`}
              pctVal={`${((p.value/Math.max(...(data?.top_products||[]).map(x=>x.value),1))*100).toFixed(0)}%`}
              color="#1d4ed8" />
          ))}
        </Section>

        {/* Top reels */}
        <Section title="Top Clips by Views">
          {(data?.top_reels || []).map((r, i) => (
            <TopRow key={i} rank={i+1} name={r.name} sub={r.sub}
              value={num(r.views)}
              pctVal={`${((r.views/Math.max(...(data?.top_reels||[]).map(x=>x.views),1))*100).toFixed(0)}%`}
              color="#7c3aed" />
          ))}
        </Section>
      </div>

      {/* ── ROW 6 — Top videos ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:14, marginTop:14 }}>
        <Section title="Top Videos by Views">
          {(data?.top_videos || []).map((v, i) => (
            <TopRow key={i} rank={i+1} name={v.name} sub={v.sub}
              value={num(v.views)}
              pctVal={`${((v.views/Math.max(...(data?.top_videos||[]).map(x=>x.views),1))*100).toFixed(0)}%`}
              color="#0d9488" />
          ))}
        </Section>
      </div>
    </div>
  )
}